import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import {
    ActionTableCell,
    CheckboxTableRow,
    ExTable,
    TableBody,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { IssueRequestForm, useDownloadCsvOrganizationsApi, useFetchOrganizationsApi, useIssueRequestFormApi } from 'api/organization'
import { Organization, OrganizationSearchForm } from 'entities/organization'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { organizationColumnInfoList, OrganizationTableData } from './organization_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { InputField } from 'components/shared/input'
import { useState } from 'react'
import { EditOrganizationForm, NewOrganizationForm } from './organization_form'
import { Flex } from 'components/shared/flex'
import { Button, Modal } from 'react-bootstrap'

type Props = {
    history: H.History
}

const OrganizationsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<OrganizationSearchForm>()
    const preSearchForm = useForm<OrganizationSearchForm>(params)
    const searchForm = useForm<OrganizationSearchForm>(params)
    const api = useFetchOrganizationsApi(searchForm)
    const downloadCsvApi = useDownloadCsvOrganizationsApi(searchForm)
    const multipleCheckForm = useMultipleCheckForm<Organization>()
    const organizationDisplaySetting = useDisplaySetting(organizationColumnInfoList(searchForm), 'Organization')
    const [selectedOrganization, setSelectedOrganization] = useState<Organization>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const addRequestForm = useForm<IssueRequestForm>({})
    const issueRequestApi = useIssueRequestFormApi()
    const [showAddRequest, setShowAddRequest] = useState<boolean>(false)
    const [hoveredItem, setHoveredItem] = useState<Organization>({})
    let timeoutId: NodeJS.Timeout

    useEffect(() => {
        api.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof OrganizationSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`organizations?${queryParams}`)
        }
    }, [searchForm.object])

    const handleClickEdit = (organization: Organization) => {
        setSelectedOrganization(organization)
        setShowEdit(true)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    useEffectSkipFirst(() => {
        globalState.setLoading(issueRequestApi.loading)
        if (issueRequestApi.isSuccess()) {
            api.execute()
            setShowAddRequest(false)
        }
    }, [issueRequestApi.loading])

    return (
        <>
            <EditOrganizationForm
                organization={selectedOrganization}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    api.execute()
                    setShowEdit(false)
                }}
            />
            <Modal show={showAddRequest} onHide={() => setShowAddRequest(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>検査依頼書の発行</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <InputField form={addRequestForm} attr="amount" required label="発行枚数" type="number" />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddRequest(false)}>
                        キャンセル
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => {
                            issueRequestApi.execute(multipleCheckForm.ids()[0] as number, addRequestForm)
                        }}
                    >
                        発行
                    </Button>
                </Modal.Footer>
            </Modal>

            <ContentHeader
                headerTitle="組織・グループ"
                left={
                    <>
                        {/* <CopyTableButton /> */}

                        <NewOrganizationForm
                            onComplete={() => {
                                api.execute()
                            }}
                        />
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} />
                    </>
                }
                right={<>{/* <DisplaySettingModal displaySettings={displaySettings} /> */}</>}
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <InputField
                        attr="name"
                        form={preSearchForm}
                        placeholder="組織名"
                        onPressEnter={() => {
                            searchForm.updateObject('name', preSearchForm.getValue('name'))
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    />
                </Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={multipleCheckForm} showEdit>
                                <ActionTableCell th width={60} />
                                <TableHeads displaySetting={organizationDisplaySetting} />
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={organizationDisplaySetting.getColumnLength() + 2}>
                            {api.response.results.map((organization: Organization) => {
                                return (
                                    <CheckboxTableRow
                                        key={`organization-${organization.id}`}
                                        item={organization}
                                        multipleCheckForm={multipleCheckForm}
                                        onClickEdit={() => handleClickEdit(organization)}
                                        onMouseEnter={() => {
                                            setHoveredItem(organization)
                                            clearTimeout(timeoutId)
                                        }}
                                        onMouseLeave={() => {
                                            clearTimeout(timeoutId)
                                            timeoutId = setTimeout(() => {
                                                setHoveredItem({})
                                            }, 100)
                                        }}
                                    >
                                        <ActionTableCell
                                            action="edit"
                                            onClick={() => {
                                                setSelectedOrganization(organization)
                                                setShowEdit(true)
                                            }}
                                        />
                                        <OrganizationTableData organization={organization} displaySetting={organizationDisplaySetting} />
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            {/* <SelectionFixedFooter
                ids={multipleCheckForm.ids()}
                onClickReset={() => multipleCheckForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="Organization"
            >
                <Button
                    variant="success"
                    disabled={multipleCheckForm.ids().length !== 1}
                    onClick={() => {
                        setShowAddRequest(true)
                    }}
                >
                    検査依頼書を発行
                </Button>
            </SelectionFixedFooter> */}
        </>
    )
}

export default withRouter(OrganizationsPage)
